import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Permissions } from '@app/core/guard/Permissions';
import {
  DfpFinancialReadingFinding,
  DfpFinancialReadingResponse,
} from '@interfaces/dfp/response/dfpFinancialReadingResponse';
import { RealtimeEventTypes } from '@interfaces/realtime/events';
import { PermissionsService } from '@services/helpers/permissions.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { DfpFinancialReadingService } from '@services/http/dfp/dfp-financial-reading.service';
import { RealtimeListRefreshService } from '@services/realtime/realtime-list-refresh.service';
import { VaultDatePipe } from '@shared/pipes/vault-date.pipe';
import { VaultDateTimePipe } from '@shared/pipes/vault-date-time.pipe';
import { SharedModule } from '@shared/shared.module';
import { Subscription, timer } from 'rxjs';
import { DfpModulesState } from '@features/dashboard/performance/pages/dfp/services/dfp-modules.state';
import { DfpStandardizationNavigationService } from '@features/dashboard/performance/pages/dfp/services/dfp-standardization-navigation.service';
import {
  FINDING_TARGET_LINK,
  describeFinancialReading,
  findingModule,
} from './financial-reading-view.util';

/** Releitura de segurança enquanto o Cog escreve: o caminho sem tempo real (hub fora do ar). */
const FALLBACK_POLL_MS = 60_000;

/**
 * Leitura do Cog dos demonstrativos (card 3801), no topo do painel "Pontos de destaque". A leitura nasce no backend
 * quando um documento contábil é validado (ou no "Gerar de novo") e fica gravada; aqui só se lê a última, e se relê no
 * evento `dfp-financial-reading.changed` do tópico da empresa. O agente não calcula: ele cita os números que o backend
 * mandou prontos, e a tela só mostra o texto.
 *
 * Quem decide se a faixa existe: o `DfpComponent` não a monta no modo somente leitura (parceiro), e ela mesma some sem
 * `DFPFINANCIALREADING_VIEW`. "Gerar agora" e "Gerar de novo" só com `DFPFINANCIALREADING_GENERATE`.
 */
@Component({
  selector: 'app-dfp-financial-reading',
  templateUrl: './financial-reading.component.html',
  styleUrls: ['./financial-reading.component.scss'],
  imports: [SharedModule, VaultDatePipe, VaultDateTimePipe],
  providers: [RealtimeListRefreshService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DfpFinancialReadingComponent {
  readonly companyId = input.required<string>();

  private readonly readingService = inject(DfpFinancialReadingService);
  private readonly toast = inject(ToastHelperService);
  private readonly standardizationNavigation = inject(DfpStandardizationNavigationService);
  /** Opcional: fora da DFP (ex.: teste isolado) o clique no achado só não liga o módulo. */
  private readonly modules = inject(DfpModulesState, { optional: true });
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly realtimeRefresh = inject(RealtimeListRefreshService);
  private readonly permissions = inject(PermissionsService);

  protected readonly canView = toSignal(
    this.permissions.hasPermission(Permissions.DfpFinancialReading.VIEW),
    { initialValue: false }
  );
  protected readonly canGenerate = toSignal(
    this.permissions.hasPermission(Permissions.DfpFinancialReading.GENERATE),
    { initialValue: false }
  );
  protected readonly canOpenStandardization = toSignal(
    this.permissions.hasPermission(Permissions.DocumentPadronization.VIEW),
    { initialValue: false }
  );

  protected readonly reading = signal<DfpFinancialReadingResponse | null>(null);
  protected readonly loading = signal(true);
  protected readonly loadFailed = signal(false);
  protected readonly requesting = signal(false);

  protected readonly view = computed(() => describeFinancialReading(this.reading()));
  protected readonly current = computed(() => this.reading()?.current ?? null);
  protected readonly targetLinks = FINDING_TARGET_LINK;

  private loadSub: Subscription | null = null;
  private pollSub: Subscription | null = null;

  constructor() {
    effect(() => {
      const companyId = this.companyId();
      const canView = this.canView();
      untracked(() => {
        this.reading.set(null);
        if (companyId && canView) this.load(false);
      });
    });

    this.realtimeRefresh
      .refresh$({
        eventType: RealtimeEventTypes.DfpFinancialReadingChanged,
        topicMatches: topic => topic.startsWith('company:'),
        shouldRefresh: envelope => {
          const current = this.companyId()?.toLowerCase();
          const eventCompany = (
            envelope.companyId ?? (envelope.payload?.['companyId'] as string | undefined)
          )?.toLowerCase();
          return !!current && current === eventCompany;
        },
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.load(true));

    this.destroyRef.onDestroy(() => {
      this.loadSub?.unsubscribe();
      this.pollSub?.unsubscribe();
    });
  }

  /** Relê a leitura. `silent` não mostra o carregando (evento de tempo real, releitura de segurança). */
  protected load(silent: boolean): void {
    if (!this.canView()) return;
    const companyId = this.companyId();
    this.loadSub?.unsubscribe();
    this.pollSub?.unsubscribe();
    if (!silent) this.loading.set(true);

    this.loadSub = this.readingService.get(companyId).subscribe({
      next: response => {
        if (companyId !== this.companyId()) return;
        this.apply(response);
        this.loadFailed.set(false);
        this.loading.set(false);
      },
      error: () => {
        // Falha na releitura silenciosa não apaga o que já está na tela.
        if (!silent || !this.reading()) this.loadFailed.set(true);
        this.loading.set(false);
      },
    });
  }

  /** "Gerar agora" / "Gerar de novo": enfileira na hora; a tela passa a "escrevendo" e o evento traz o resto. */
  protected generate(): void {
    if (!this.canGenerate() || this.requesting()) return;
    const companyId = this.companyId();
    this.requesting.set(true);
    this.readingService.generate(companyId).subscribe({
      next: response => {
        this.requesting.set(false);
        if (companyId !== this.companyId()) return;
        if (response) this.apply(response);
        else this.load(true);
      },
      error: (error: unknown) => {
        this.requesting.set(false);
        const errors = (error as { errors?: { message: string }[] })?.errors ?? [];
        this.toast.showToastError(
          'Não foi possível pedir a leitura',
          errors[0]?.message ??
            'O pedido não chegou ao servidor. Os números da tela não mudam; tente Gerar de novo em alguns minutos.'
        );
      },
    });
  }

  /** Liga o módulo do achado (se a pessoa o tinha desligado) e rola até ele. */
  protected openFinding(finding: DfpFinancialReadingFinding): void {
    const module = findingModule(finding.target);
    this.modules?.ensureOn(module);
    // Próximo ciclo: o bloco recém-ligado precisa sair do `[hidden]` antes de rolar.
    setTimeout(() => {
      this.document
        .getElementById(`statement-module-${module}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  protected openStandardization(): void {
    this.standardizationNavigation.goToList(this.companyId());
  }

  private apply(response: DfpFinancialReadingResponse | null): void {
    this.reading.set(response ?? { state: 'none', stale: false });
    this.pollSub?.unsubscribe();
    if (this.view().shouldPoll) {
      this.pollSub = timer(FALLBACK_POLL_MS).subscribe(() => this.load(true));
    }
  }
}
